"use client";
import React, { createContext, useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

const TimesheetContext = createContext();

// CHANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON, which previously caused res.json() to throw.
const safeParseJSON = async (res) => {
  try {
    const text = await res.text();
    if (!text) {
      return { message: "Empty response from server" };
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      return { message: "Invalid response format from server" };
    }
  } catch (readErr) {
    return { message: "Failed to read server response" };
  }
};

export const TimesheetProvider = ({ children }) => {
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Get token from localStorage
  const getToken = () => localStorage.getItem("token");

  // ✅ Fetch all timesheets — fetch operation, no toasts
  // CHANGED: Removed toast.error call — this is a GET/fetch operation, not a user action.
  const fetchTimesheets = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) {
        return; // simply skip API call
      }
      const res = await fetch(`${BASE_URL}/timesheets`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to fetch timesheets");
      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      setTimesheets(data.data || []);
    } catch (err) {
      console.error(err);
      setError(err.message);
      // CHANGED: removed toast.error(err.message || "Failed to fetch timesheets")
    } finally {
      setLoading(false);
    }
  };

  // ✅ Create new timesheet — user action, toasts preserved
  const createTimesheet = async (timesheetData) => {
    try {
      const token = getToken();
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const create_audit_id = user?.id;

      const res = await fetch(`${BASE_URL}/timesheets`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...timesheetData,
          create_audit_id: create_audit_id,
        }),
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (!res.ok) {
        throw new Error(data.message || `Failed to create timesheet (Status ${res.status})`);
      }

      console.log("✅ Timesheet created:", data);
      toast.success(data.message || "Timesheet created successfully"); // KEPT: user-initiated action

      // Refresh list after adding
      await fetchTimesheets();
      return data;
    } catch (err) {
      console.error("🔥 Error in createTimesheet:", err);
      toast.error(err.message || "Failed to create timesheet"); // KEPT: user-initiated action
    }
  };


  // ✅ Approve / Update timesheet — user action, toasts preserved
 const updateTimesheetStatus = async (id, status) => {
    try {
      const token = getToken();

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const update_audit_id = user?.id;

      const res = await fetch(`${BASE_URL}/timesheets/${id}/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status,
          update_audit_id: update_audit_id,
        }),
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (!res.ok) {
        throw new Error(data.message || `Failed to update timesheet (Status ${res.status})`);
      }

      toast.success(data.message || "Timesheet status updated successfully"); // KEPT: user-initiated action
      await fetchTimesheets(); // refresh list
      return data;
    } catch (err) {
      console.error("🔥 Error in updateTimesheetStatus:", err);
      toast.error(err.message || "Failed to update timesheet status"); // KEPT: user-initiated action
    }
  };

  // TimesheetContext.js
  // ✅ Update timesheet — user action, toasts preserved
  const updateTimesheet = async (id, payload) => {
    try {
      const token = getToken(); // your auth token
      const res = await fetch(`${BASE_URL}/timesheets/${id}`, {
        method: "PUT", // or "PUT" if your backend supports it
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (!res.ok) {
        throw new Error(data.message || `Failed to update timesheet (Status ${res.status})`);
      }

      toast.success(data.message || "Timesheet updated successfully"); // KEPT: user-initiated action
      await fetchTimesheets(); // refresh list after update
      return data;
    } catch (err) {
      console.error("🔥 Error in updateTimesheet:", err);
      toast.error(err.message || "Failed to update timesheet"); // KEPT: user-initiated action
      throw err;
    }
  };



  useEffect(() => {
    fetchTimesheets();
  }, []);

  return (
    <TimesheetContext.Provider
      value={{ timesheets, loading, createTimesheet, error, fetchTimesheets, updateTimesheet, updateTimesheetStatus }}
    >
      {children}
    </TimesheetContext.Provider>
  );
};

// Custom hook
export const useTimesheets = () => useContext(TimesheetContext);